import {
  FACTORS,
  TOP_SECTIONS,
  MAX_ITEMS_WARNING,
  FACTOR_LABELS,
  SECTION_LABELS,
  type DriehoekData,
  type Factor,
  type TopSection,
} from "../types/driehoek-data";
import { CollapsibleSection } from "./CollapsibleSection";

interface ItemListsProps {
  data: DriehoekData;
  onBasisgegevensChange: (value: string) => void;
  onAddFactor: (factor: Factor, text: string) => void;
  onAddSection: (section: TopSection, text: string) => void;
  onEditFactor: (id: string, text: string) => void;
  onDeleteFactor: (id: string) => void;
  onEditSection: (id: string, text: string) => void;
  onDeleteSection: (id: string) => void;
  onClear: () => void;
}

function ListBlock({
  items,
  placeholder,
  onAdd,
  onEdit,
  onDelete,
}: {
  items: { id: string; text: string }[];
  placeholder: string;
  onAdd: (text: string) => void;
  onEdit: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) {
  const handleAdd = (input: HTMLInputElement) => {
    const text = input.value.trim();
    if (!text) return;
    onAdd(text);
    input.value = "";
  };

  return (
    <div className="cd-factor-input">
      <div className="cd-add-row">
        <input
          type="text"
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd(e.currentTarget);
          }}
        />
        <button
          type="button"
          className="cd-btn-add"
          aria-label="Toevoegen"
          onClick={(e) => {
            const input = e.currentTarget.previousElementSibling as HTMLInputElement;
            handleAdd(input);
          }}
        >
          +
        </button>
      </div>
      <ul className="cd-item-list">
        {items.map((item) => (
          <li key={item.id}>
            <span>{item.text}</span>
            <button
              type="button"
              className="cd-btn-icon"
              aria-label="Bewerken"
              onClick={() => {
                const t = window.prompt("Bewerk tekst:", item.text);
                if (t !== null && t.trim()) onEdit(item.id, t.trim());
              }}
            >
              ✎
            </button>
            <button
              type="button"
              className="cd-btn-icon"
              aria-label="Verwijderen"
              onClick={() => onDelete(item.id)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function sectionDefaultOpen(count: number): boolean {
  return count > 0;
}

export function ItemLists({
  data,
  onBasisgegevensChange,
  onAddFactor,
  onAddSection,
  onEditFactor,
  onDeleteFactor,
  onEditSection,
  onDeleteSection,
  onClear,
}: ItemListsProps) {
  const handleAddFactor = (factor: Factor, text: string) => {
    onAddFactor(factor, text);
    if (data.items.length + 1 > MAX_ITEMS_WARNING) {
      window.alert(
        `Je hebt nu ${data.items.length + 1} items. Bij veel items kunnen teksten overlappen.`
      );
    }
  };

  const topSectionCount = TOP_SECTIONS.reduce(
    (sum, s) => sum + data.topSections[s].length,
    0
  );
  const fillHint = [
    data.items.length > 0 ? `${data.items.length} factoren` : null,
    topSectionCount > 0 ? `${topSectionCount} notities` : null,
    data.basisgegevens.trim() ? "basisgegevens" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="cd-input-panel jari-card">
      <CollapsibleSection
        id="cd-input-panel"
        title="Gegevens invullen"
        variant="panel"
        defaultOpen
        hint={fillHint || undefined}
      >
        <div className="cd-input-grid">
          <div className="cd-panel-section">
            <label htmlFor="basisgegevens">Basisgegevens</label>
            <textarea
              id="basisgegevens"
              className="jari-textarea cd-basisgegevens-input"
              rows={4}
              placeholder="Voer basisgegevens in…"
              value={data.basisgegevens}
              onChange={(e) => onBasisgegevensChange(e.target.value)}
            />
          </div>

          <div className="cd-panel-section cd-top-sections">
            <h3 className="cd-panel-section__heading">Hypotheses, Ideeën, Vragen</h3>
            <div className="cd-top-sections-stack">
              {TOP_SECTIONS.map((section) => (
                <CollapsibleSection
                  key={section}
                  id={`cd-input-${section}`}
                  title={SECTION_LABELS[section]}
                  variant="section"
                  count={data.topSections[section].length}
                  defaultOpen={sectionDefaultOpen(data.topSections[section].length)}
                >
                  <ListBlock
                    items={data.topSections[section]}
                    placeholder={`Voeg ${SECTION_LABELS[section].toLowerCase()} toe…`}
                    onAdd={(text) => onAddSection(section, text)}
                    onEdit={onEditSection}
                    onDelete={onDeleteSection}
                  />
                </CollapsibleSection>
              ))}
            </div>
          </div>

          <div className="cd-panel-section">
            <h3 className="cd-panel-section__heading">Driehoek factoren</h3>
            <div className="cd-factors-row">
              {FACTORS.map((factor) => (
                <div key={factor} className="cd-factor-input">
                  <h4>{FACTOR_LABELS[factor]}</h4>
                  <ListBlock
                    items={data.items.filter((i) => i.factor === factor)}
                    placeholder="Voeg toe…"
                    onAdd={(text) => handleAddFactor(factor, text)}
                    onEdit={onEditFactor}
                    onDelete={onDeleteFactor}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="cd-panel-actions">
          <button type="button" className="jari-btn" onClick={onClear}>
            Alles wissen
          </button>
        </div>
      </CollapsibleSection>
    </div>
  );
}
